/**
 * Address Interface
 * Represents structured address information
 * 
 * @interface Address
 * @author Financial Insights Platform
 * @version 1.0
 */

export interface Address {
  /**
   * Address line 1 (street address)
   * @type {string}
   */
  addressLine1?: string;

  /**
   * Address line 2 (apartment, suite, unit, etc.)
   * @type {string}
   */
  addressLine2?: string;

  /**
   * City
   * @type {string}
   */
  city?: string;

  /**
   * State or province
   * @type {string}
   */
  state?: string;

  /**
   * Zip code or postal code
   * @type {string}
   */
  zipCode?: string;

  /**
   * Country
   * @type {string}
   */
  country?: string;
}

/**
 * Get formatted address as a single string
 * @param address Address object
 * @returns Formatted address string
 */
export function getFormattedAddress(address?: Address): string {
  if (!address) return "";
  
  const parts: string[] = [];
  
  if (address.addressLine1?.trim()) {
    parts.push(address.addressLine1);
  }
  
  if (address.addressLine2?.trim()) {
    parts.push(address.addressLine2);
  }
  
  if (address.city?.trim()) {
    parts.push(address.city);
  }
  
  if (address.state?.trim()) {
    parts.push(address.state);
  }
  
  if (address.zipCode?.trim()) {
    parts.push(address.zipCode);
  }
  
  if (address.country?.trim()) {
    parts.push(address.country);
  }
  
  return parts.join(", ");
}

/**
 * Check if address is empty
 * @param address Address object
 * @returns true if all fields are empty
 */
export function isAddressEmpty(address?: Address): boolean {
  if (!address) return true;
  
  return !address.addressLine1?.trim() &&
         !address.addressLine2?.trim() &&
         !address.city?.trim() &&
         !address.state?.trim() &&
         !address.zipCode?.trim() &&
         !address.country?.trim();
}
